import {transformAPIResponseIntoCapitalizedJSON, transformCapitalizedJSONIntoSnakeCase} from "./utility";
import toaster from "./components/common/toaster";

function displayToaster(message, prependMessage) {
    if (!prependMessage) toaster.error(message);
    else toaster.error(prependMessage + ' ' + message);
}

function showToasterError(data, message) {
    if (data.message) {
        displayToaster(data.message, message);
    }

    if (data.errors && !_.isEmpty(data.errors)) {
        Object.keys(data.errors).map(errorKey => {
            const errors = data.errors[errorKey];
            errors.forEach(error => {
                if (_.isObject(error)) {
                    const firstProperty = Object.keys(error)[0];
                    displayToaster(error[firstProperty], message);
                } else {
                    displayToaster(error, message);
                }
            })
        })
    }

    return data;
}

export {showToasterError};

/**
 * @typedef APISettings
 * @property dontSnakeCase {Boolean}
 * @property timeout {Number} Default is 2000ms
 */

export default class API {
    /**
     * @param url {String}
     * @param [settings] {APISettings}
     */
    constructor(url, settings = {}) {
        const {dontSnakeCase = false, dontKebabCase = false, timeout = 2000} = settings;
        if (!url) throw Error('API need url!');

        this.url = url;
        this.errorTimeout= timeout;
        this.dontSnakeCase = dontSnakeCase;
        this.dontKebabCase = dontKebabCase;
    }

    /**
     * @param data {Object}
     * @param [errorCallback] {Function}
     * @returns {Promise<{errored: boolean}|{data: *, response: Response, errored: boolean}|*|string>}
     */
    async post(data = {}, errorCallback = this.handleErrors) {
        const body = this.prepareBody(data);

        const response = await fetch(this.url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Csrf-Token': Laravel.csrfToken
            },
            body
        });

        if (!response.ok) {
            const data = await errorCallback(response, this.errorTimeout);
            return {errored: true, response, data};
        }

        const contentType = response.headers.get("Content-Type");
        if (contentType.includes('json')) {
            const returnedData = await response.json();

            if (this.dontKebabCase) {
                if (returnedData.data) return returnedData.data;
                else return returnedData;
            }

            if (returnedData.data) return transformAPIResponseIntoCapitalizedJSON(returnedData.data);
            return transformAPIResponseIntoCapitalizedJSON(returnedData);
        }

        if (contentType.includes('text/html')) {
            console.warn('API returned text/html response and we can only deal with json');
            return {errored: false};
        }
    }

    async get(headers) {
        const init = {method: 'GET'};

        if (headers) init.headers = headers;

        const response = await fetch(this.url, init);

        if (!response.ok) {
            await this.handleErrors(response, this.errorTimeout);
            return {errored: true};
        }

        const contentType = response.headers.get("Content-Type");
        if (contentType.includes('json')) {
            const returnedData = await response.json();

            if (this.dontKebabCase) {
                if (returnedData.data) return returnedData.data;
                else return returnedData;
            }

            if (returnedData.data) return transformAPIResponseIntoCapitalizedJSON(returnedData.data);
            return transformAPIResponseIntoCapitalizedJSON(returnedData);
        }

        if (contentType.includes('text/html')) {
            console.warn('API returned text/html response and we can only deal with json');
            return {errored: false};
        }
    }

    prepareBody(data) {
        if (this.dontSnakeCase) return JSON.stringify(data);
        if (!data) return null;
        const snakeCaseData = transformCapitalizedJSONIntoSnakeCase(data);
        return JSON.stringify(snakeCaseData);
    }

    async handleErrors(response, timeout) {
        if (response.status === 401) {
            const message = `Unauthorized. Please login!`;
            toaster.error(message);
            setTimeout(() => {
                window.location = '/login';
            }, timeout)
            return;
        }

        const contentType = response.headers.get("Content-Type");
        if (contentType.includes('json')) {
            const data = await response.json();

            return showToasterError(data);
        }

        const message = response.statusText;
        toaster.error(message);
    }
}
